import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, iif, Observable, of, Subscription, throwError } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons/faEllipsisH';
import { faFileDownload } from '@fortawesome/free-solid-svg-icons/faFileDownload';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons/faEnvelopeOpenText';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons/faFileCsv';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { HelperService } from '../../../services/helper/helper.service';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { Download } from '../../../helpers/download/download';
import { JobResultInterface } from '../../../models/interface/jobresult.interface';
import { catchError, delay, mergeMap, repeat, skip, take } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueueActionTypes } from '../../../store/queue/queue.action';
import { StudyStore } from '../../../store/study/component-store/study.store';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Component({
  selector: 'app-dialog-study-report',
  templateUrl: './dialog-study-report.component.html',
  styleUrls: ['./dialog-study-report.component.scss'],
  providers: [StudyStore]
})
export class DialogStudyReportComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator;

  // Icons
  faHands = faHands;
  faHandsHelping = faHandsHelping;
  faEnvelopeOpenText = faEnvelopeOpenText;
  faKey = faKey;
  faFileCsv = faFileCsv;
  faFileDownload = faFileDownload;
  faTimes = faTimes;
  faEllipsisH = faEllipsisH;
  faRedo = faRedo;
  faClock = faClock;

  public isLoading$: Observable<boolean>;

  // Data provided by StudyService
  public group: StudyInterface;

  public param;

  // List of roles
  public roles = ['study.owner', 'study.ecoachmanager', 'study.ecoach', 'study.access'];
  public selectedRole = 'study.access';

  public isReviewer = false;
  public isReviewerCollaborator = false;

  public includeMeta = false;
  public includeDiaryInstance = false;

  // Form submission
  public submitted = false;

  public downloadCSVReportResponse: BehaviorSubject<string> = new BehaviorSubject<string>('DEFAULT');

  public download$: Observable<Download>;

  public jobresults$: Observable<Array<JobResultInterface>>;

  public selectedTabIndex = 0;

  public showDownloadHintSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public pagedJobResults$: Observable<Array<JobResultInterface>>;

  private isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private pagedJobResultsSubject: BehaviorSubject<Array<JobResultInterface>> = new BehaviorSubject<Array<JobResultInterface>>([]);
  private jobresultsSubject: BehaviorSubject<Array<JobResultInterface>> = new BehaviorSubject<Array<JobResultInterface>>([]);

  private getStudyCSVReportQueueResponse$: Observable<any>;

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private helperService: HelperService,
    private studyStore: StudyStore,
    private actions$: Actions,
    private store: Store
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.jobresults$ = this.jobresultsSubject.asObservable();
    this.pagedJobResults$ = this.pagedJobResultsSubject.asObservable();
    this.getStudyCSVReportQueueResponse$ = this.studyStore.getStudyCSVReportQueueResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.group = this.data.group;

    const poll = of({}).pipe(
      mergeMap(_ => {
        if (this.jobresultsSubject.value.filter(job => job.attributes.finished === 0).length > 0) {
          this.loadJobResults();
        }
        return of({});
      }),
      catchError(e => of(null)),
      delay(30000),
      repeat()
    );

    this.subscriptions.push(poll.subscribe());

    this.loadJobResults();
  }

  public getInstanceReportCSV(group: StudyInterface) {
    if (this.downloadCSVReportResponse.value === 'FAILURE' || this.downloadCSVReportResponse.value === 'SUCCESS') {
      this.downloadCSVReportResponse.next('DEFAULT');
      return;
    }

    this.showDownloadHintSubject.next(false);

    const meta = this.includeMeta ? 1 : undefined;
    const diaryInstance = this.includeDiaryInstance ? 1 : undefined;

    this.submitted = true;
    if (this.downloadCSVReportResponse.value === 'DEFAULT') {
      this.downloadCSVReportResponse.next('LOADING');
      this.studyStore.getStudyCSVReportQueue({ studyId: group.id, meta, diaryInstances: diaryInstance });
      this.getStudyCSVReportQueueResponse$
        .pipe(
          skip(1),
          take(1),
          mergeMap((result: any) => iif(() => result instanceof HttpErrorResponse, throwError(result), of(result)))
        )
        .subscribe(
          (result: any) => {
            this.loadJobResults();
            this.downloadCSVReportResponse.next('SUCCESS');
            this.showDownloadHintSubject.next(true);
            setTimeout(() => {
              this.downloadCSVReportResponse.next('DEFAULT');
              this.submitted = false;
            }, 2500);
          },
          () => {
            this.downloadCSVReportResponse.next('FAILURE');
            setTimeout(() => {
              this.downloadCSVReportResponse.next('DEFAULT');
              this.submitted = false;
            }, 2500);
          }
        );
    }
  }

  public setSelectedTabIndex(event): void {
    this.selectedTabIndex = event;
    this.showDownloadHintSubject.next(false);
  }

  public loadJobResults(): void {
    this.store.dispatch({ type: QueueActionTypes.getAllJobResultsOfUserType, payload: {} });
    this.subscriptions.push(
      this.actions$
        .pipe(ofType(QueueActionTypes.getAllJobResultsOfUserSuccessType, QueueActionTypes.getAllJobResultsOfUserErrorType), take(1))
        .subscribe((result: any) => {
          if (result.type === QueueActionTypes.getAllJobResultsOfUserSuccessType) {
            const allJobs: Array<JobResultInterface> = result['response']['body']['data'];
            this.jobresultsSubject.next(
              allJobs
                .filter(
                  (job: JobResultInterface) =>
                    job.attributes.entity_id.toString() === this.group.id.toString() && job.attributes.type === 'Study'
                )
                .sort((a, b) => b.id - a.id)
            );
            this.pagedJobResultsSubject.next(this.jobresultsSubject.value.slice(0, 20));
            this.isLoadingSubject.next(false);
            if (this.paginator?.paginator) {
              this.paginator.paginator.firstPage();
            }
          }
        })
    );
  }

  public updatePagedJobStudies(event: any) {
    if (event) {
      this.pagedJobResultsSubject.next(event);
    }
  }

  public trackByJobId(index: number, element: any): number {
    return element.id;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
  }
}
